import type { VideoItem, CtaOverlay } from "../shared/types";
import type { SingleEmbedProps } from "../single/SingleEmbed";

export function parseVideoItem(raw: Record<string, unknown>): VideoItem {
  return {
    id: String(raw.id ?? Math.random()),
    title: String(raw.title ?? ""),
    description: raw.description ? String(raw.description) : undefined,
    src: String(raw.src ?? ""),
    poster: raw.poster ? String(raw.poster) : undefined,
    thumbnailStoryboardUrl: raw.thumbnailStoryboardUrl
      ? String(raw.thumbnailStoryboardUrl)
      : undefined,
    duration: raw.duration != null ? Number(raw.duration) : undefined,
    sponsor: raw.sponsor as VideoItem["sponsor"],
    endCard: raw.endCard as VideoItem["endCard"],
    ctaTimestamps: raw.ctaTimestamps as VideoItem["ctaTimestamps"],
  };
}

export function parseSingleProps(el: Element): SingleEmbedProps {
  const d = (el as HTMLElement).dataset;
  const overlays: CtaOverlay[] = d.overlays ? JSON.parse(d.overlays) : [];

  const autoplay = d.autoplay === "true";
  const muted = d.muted === "true";
  const gaMeasurementId = d.gaMeasurementId;
  const startTime =
    d.startTime != null ? Number(d.startTime) : undefined;
  const momentsNavPosition =
    d.momentsNavPosition === "below" ? "below" as const
    : d.momentsNavPosition === "none" || d.momentsNavPosition === "false" ? "none" as const
    : undefined;
  const share = d.share === "true";
  const momentNavBehavior =
    d.momentNavBehavior === "seek" ? ("seek" as const)
    : d.momentNavBehavior === "moment" ? ("moment" as const)
    : undefined;

  // API-driven: data-api-key + data-video-id
  if (d.apiKey && d.videoId) {
    return {
      apiKey: d.apiKey,
      videoId: d.videoId,
      overlays,
      autoplay,
      muted,
      gaMeasurementId,
      startTime,
      momentsNavPosition,
      momentNavBehavior,
      share,
    };
  }

  // Pre-fetched: data-src (required)
  return {
    video: {
      id: d.id ?? String(Math.random()),
      title: d.title ?? "",
      description: d.description,
      src: d.src ?? "",
      poster: d.poster,
      thumbnailStoryboardUrl: d.thumbnailStoryboardUrl,
      duration: d.duration != null ? Number(d.duration) : undefined,
      sponsor: d.sponsor ? JSON.parse(d.sponsor) : undefined,
    } satisfies VideoItem,
    overlays,
    autoplay,
    muted,
    gaMeasurementId,
    startTime,
    momentsNavPosition,
    momentNavBehavior,
    share,
  };
}

function parseMaxVideos(d: DOMStringMap): number | undefined {
  // Blank or missing means no limit.
  return d.maxVideos ? Number(d.maxVideos) : undefined;
}

export function parsePlaylistProps(el: Element) {
  const d = (el as HTMLElement).dataset;
  const overlays: CtaOverlay[] = d.overlays ? JSON.parse(d.overlays) : [];
  const initialIndex = d.initialIndex ? Number(d.initialIndex) : 0;

  // API-driven: data-api-key + data-playlist-id
  if (d.apiKey && d.playlistId) {
    return {
      apiKey: d.apiKey,
      playlistId: d.playlistId,
      overlays,
      initialIndex,
      pageSize: d.pageSize != null ? Number(d.pageSize) : undefined,
      maxVideos: parseMaxVideos(d),
    };
  }

  // Pre-fetched: data-videos (JSON array)
  const videos: VideoItem[] = JSON.parse(d.videos ?? "[]").map(parseVideoItem);
  return { videos, overlays, initialIndex, maxVideos: parseMaxVideos(d) };
}

export function parseReelsProps(el: Element) {
  const d = (el as HTMLElement).dataset;
  const overlays: CtaOverlay[] = d.overlays ? JSON.parse(d.overlays) : [];

  // API-driven: data-api-key + data-playlist-id
  if (d.apiKey && d.playlistId) {
    return {
      apiKey: d.apiKey,
      playlistId: d.playlistId,
      overlays,
      pageSize: d.pageSize != null ? Number(d.pageSize) : undefined,
      maxVideos: parseMaxVideos(d),
    };
  }

  // Pre-fetched: data-videos (JSON array)
  const videos: VideoItem[] = JSON.parse(d.videos ?? "[]").map(parseVideoItem);
  return { videos, overlays, maxVideos: parseMaxVideos(d) };
}
